import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import bind from '../bind';
import { withUserContext, IUserContext } from './user_context_provider';
import { mparticleEmailFields } from '../mparticle_email_fields_helper';

import { post } from '../http_client';
import { CORE_WEB_ORIGIN } from '@reverbdotcom/env';

const SUBSCRIBE_URL = '/mailchimp_subscriptions?list_id=';
const DEFAULT_LIST_ID = '343a88cca9';

interface ISubscribeRequest {
  email: string;
  consented_to_gdpr: boolean;
  signup_reason: string;
  mparticle_id: string;
  mparticle_device_id: string;
}

interface IProps extends IUserContext {
  signupReason: string;
  listId?: string;
  title?: string;
  subtitle?: string;
  actionColor?: string;
  successTitle?: string;
  successMessage?: string;
}

interface IState {
  submitted: boolean;
  disableSubmit: boolean;
  error: boolean;
  invalidEmail: boolean;
  emailTooLong: boolean;
  email: string;
}

export class NewsletterSignup extends React.Component<IProps, IState> {
  state = {
    submitted: false,
    disableSubmit: false,
    error: false,
    invalidEmail: false,
    emailTooLong: false,
    email: '',
  };

  get gdpr() {
    return this.props.user.gdpr;
  }

  get email() {
    return this.state.email;
  }

  get subscribeUrl() {
    return `SUBSCRIBE_URL${this.props.listId || DEFAULT_LIST_ID}`;
  }

  @bind
  success() {
    this.setState({ submitted: true, disableSubmit: false, error: false });
  }

  @bind
  fail() {
    this.setState({ submitted: false, disableSubmit: false, error: true });
  }

  @bind
  invalidEmail() {
    this.setState({
      submitted: false,
      disableSubmit: false,
      error: true,
      invalidEmail: true,
      emailTooLong: false,
    });
  }

  @bind
  emailTooLong() {
    this.setState({
      submitted: false,
      disableSubmit: false,
      error: true,
      invalidEmail: false,
      emailTooLong: true,
    });
  }

  @bind
  async onSubmit(e) {
    e.preventDefault();
    if (!this.email) { return; }
    if (this.email.length > 255) {
      this.emailTooLong(); 
      return;
    }
    this.setState({ disableSubmit: true });

    const params = {
      email: this.email,
      signup_reason: this.props.signupReason,
      ...mparticleEmailFields(),
    } as ISubscribeRequest;

    if (this.gdpr) { params.consented_to_gdpr = true; }

    try {
      const res = await post(CORE_WEB_ORIGIN + SUBSCRIBE_URL, this.props.user, {
        body: JSON.stringify(params),
        headers: { 'content-type': 'application/json' },
      });

      if (res.ok) {
        this.success();
      } else {
        this.invalidEmail();
      }
    } catch (err) {
      this.fail();
    }
  }

  @bind
  onInputChange(e) {
    this.setState({ email: e.target.value });
  }

  renderSuccess() {
    return (
      <div data-success className="padding-1 bg-white color-gray">
        <h3 className="size-120">
          {this.props.successTitle || I18n.t('commons.newsletterSignup.successTitle')}
        </h3>
        <p className="mb-0">
          {this.props.successMessage || I18n.t('commons.newsletterSignup.successMessage')}
        </p>
      </div>
    );
  }

  renderErrorMessage() {
    if (!this.state.error) { return; }

    let message;

    if (this.state.emailTooLong) {
      message = 'emailTooLongMessage';
    } else if (this.state.invalidEmail) {
      message = 'invalidEmailMessage';
    } else {
      message = 'errorMessage';
    }


    return (
      <div data-error className="weight-bold mtb-half size-80">
        {I18n.t(`commons.newsletterSignup.${message}`)}
      </div>
    );
  }

  renderForm() {
    if (this.state.submitted) {
      return this.renderSuccess();
    }

    const buttonClasses = classNames(
      'input-group__button button',
      { 'button--primary': this.props.actionColor === 'orange' },
    );

    return (
      <form onSubmit={this.onSubmit}>
        <h3 className="newsletter-signup__heading">
          {this.props.title || I18n.t('commons.newsletterSignup.headingNewsletter')}
        </h3>
        {this.props.subtitle &&
          <p className="newsletter-signup__subheading">
            {this.props.subtitle}
          </p>
        }
        {this.renderErrorMessage()}
        <div className="input-group">
          <input
            type="email"
            name="email"
            className="input-group__input"
            placeholder={I18n.t('commons.newsletterSignup.placeholder')}
            onChange={this.onInputChange}
          />

          {/* nasty class stacking here, temporary until we enhance the sass-core styles */}
          <button
            type="submit"
            disabled={this.state.disableSubmit}
            className={buttonClasses}
          >
            {I18n.t('commons.newsletterSignup.ctaNewsletter')}
          </button>
        </div>
        <div className="newsletter-signup__fineprint">
          {I18n.t(
            this.gdpr ? 'commons.newsletterSignup.gdprTerms' : 'commons.newsletterSignup.terms',
            { buttonText: I18n.t('commons.newsletterSignup.ctaNewsletter') },
          )}
        </div>
      </form>
    );
  }

  render() {
    return (
      <div className="newsletter-signup">
        {this.renderForm()}
      </div>
    );
  }
}

export default withUserContext(NewsletterSignup);
